// Per-study invitation mail template editor (the "✉ Email Template" toolbar button).
// Korean and English are edited and saved independently; a language that has never been
// saved shows the default from emailTemplates.js.
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { sfqApi } from "../../../api/feasibility";
import EmailComposer from "./EmailComposer";
import { DEFAULT_TEMPLATES, SAMPLE_VARS, hasRequiredVars } from "./emailTemplates";
import {
  Backdrop, Modal, Header, Title, CloseBtn, Body, Footer, Btn,
  LangTabs, LangTab, Loading,
} from "./emailModalUI";

const Meta = styled.div`
  font-size: 11px;
  color: #adb5bd;
  align-self: center;
  margin-right: auto;
`;

const LANGS = [
  { key: "ko", label: "한국어" },
  { key: "en", label: "English" },
];

export default function EmailTemplateModal({ sfqId, onClose }) {
  const [lang, setLang]       = useState("ko");
  const [drafts, setDrafts]   = useState(null);   // { ko: {subject, bodyHtml}, en: {...} }
  const [meta, setMeta]       = useState({});     // { ko: {updatedBy, updatedAt}, ... }
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    let alive = true;
    sfqApi.getEmailTemplate(sfqId)
      .then((res) => {
        if (!alive) return;
        const saved = res.data.data || {};
        // No saved row for a language → seed the editor from the frontend default.
        setDrafts({
          ko: pick(saved.ko) || { ...DEFAULT_TEMPLATES.ko },
          en: pick(saved.en) || { ...DEFAULT_TEMPLATES.en },
        });
        setMeta({ ko: saved.ko, en: saved.en });
      })
      .catch(() => {
        if (alive) setDrafts({ ko: { ...DEFAULT_TEMPLATES.ko }, en: { ...DEFAULT_TEMPLATES.en } });
      });
    return () => { alive = false; };
  }, [sfqId]);

  function pick(row) {
    return row ? { subject: row.subject, bodyHtml: row.bodyHtml } : null;
  }

  function patch(next) {
    setDrafts((d) => ({ ...d, [lang]: { ...d[lang], ...next } }));
  }

  function resetToDefault() {
    if (!window.confirm(`${LANGS.find((l) => l.key === lang).label} 템플릿을 기본값으로 되돌립니다. 계속할까요?`)) return;
    setDrafts((d) => ({ ...d, [lang]: { ...DEFAULT_TEMPLATES[lang] } }));
  }

  async function handleSave() {
    const draft = drafts[lang];
    if (!draft.subject?.trim()) { alert("제목을 입력해 주세요."); return; }
    if (!hasRequiredVars(draft.bodyHtml)) {
      alert("본문에 설문 링크 변수 {{publicUrl}}가 있어야 저장할 수 있습니다.");
      return;
    }
    setSaving(true);
    try {
      await sfqApi.saveEmailTemplate(sfqId, { lang, subject: draft.subject, bodyHtml: draft.bodyHtml });
      alert("템플릿이 저장되었습니다.");
      onClose();
    } catch (err) {
      alert(err?.response?.data?.message || "템플릿 저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  }

  const savedAt = meta[lang]?.updatedAt;

  return (
    <Backdrop>
      <Modal $wide>
        <Header>
          <Title>초대 메일 템플릿</Title>
          <CloseBtn onClick={onClose}>×</CloseBtn>
        </Header>

        <Body>
          <LangTabs>
            {LANGS.map((l) => (
              <LangTab key={l.key} type="button" $active={lang === l.key} onClick={() => setLang(l.key)}>
                {l.label}
                {!meta[l.key] && <span title="아직 저장된 적 없음 (기본값)"> ·</span>}
              </LangTab>
            ))}
          </LangTabs>

          {!drafts ? (
            <Loading>불러오는 중...</Loading>
          ) : (
            <EmailComposer
              sfqId={sfqId}
              subject={drafts[lang].subject}
              bodyHtml={drafts[lang].bodyHtml}
              onChange={patch}
              vars={SAMPLE_VARS[lang]}
            />
          )}
        </Body>

        <Footer>
          <Meta>
            {savedAt
              ? `최종 수정: ${new Date(savedAt).toLocaleString("ko-KR")}`
              : "저장된 템플릿 없음 — 기본값을 표시하고 있습니다."}
          </Meta>
          <Btn type="button" onClick={resetToDefault} disabled={!drafts || saving}>기본값으로</Btn>
          <Btn type="button" onClick={onClose} disabled={saving}>닫기</Btn>
          <Btn type="button" $primary onClick={handleSave} disabled={!drafts || saving}>
            {saving ? "저장 중..." : "저장"}
          </Btn>
        </Footer>
      </Modal>
    </Backdrop>
  );
}
